// src/components/KnowledgeGraphVisualization.js
import React, { useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3'; // Make sure d3 is installed (npm install d3)

const KnowledgeGraphVisualization = ({ graphData }) => {
    const svgRef = useRef();
    const containerRef = useRef(); // Ref for the container div
    const zoomFunctionsRef = useRef({}); // Store zoom functions

    // Memoize processed data to avoid recalculation on every render unless graphData changes
    const processedData = useMemo(() => {
        if (!graphData || !graphData.nodes || !graphData.links) {
            return { nodes: [], links: [] };
        }
        
        // Clone nodes to avoid mutating original data
        const nodes = graphData.nodes.map(node => ({ ...node }));
        
        // Create nodeMap for quick lookup
        const nodeMap = new Map(nodes.map(node => [node.id, node]));
        
        // Process links - D3 needs node objects, not just IDs
        const links = graphData.links
            .map((link, index) => {
                const sourceNode = nodeMap.get(link.source);
                const targetNode = nodeMap.get(link.target);
                
                if (!sourceNode || !targetNode) {
                    console.warn(`Link ${index} missing nodes:`, {
                        sourceId: link.source,
                        targetId: link.target,
                        hasSource: !!sourceNode,
                        hasTarget: !!targetNode
                    });
                }
                
                return {
                    ...link,
                    source: sourceNode,
                    target: targetNode
                };
            })
            .filter(link => link.source && link.target); // Filter out links with missing nodes

        return { nodes, links };
    }, [graphData]);

    useEffect(() => {
        if (!processedData.nodes.length || !svgRef.current || !containerRef.current) return;

        const container = d3.select(containerRef.current);
        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove(); // Clear previous elements

        // Get container dimensions for centering
        const width = container.node().getBoundingClientRect().width;
        const height = container.node().getBoundingClientRect().height || 600; // Default height if needed

        svg.attr('width', width)
           .attr('height', height);

        // Create a group element for zoom/pan transformations
        const g = svg.append('g');

        // --- Setup Zoom Functionality ---
        const zoom = d3.zoom()
            .scaleExtent([0.1, 4]) // Zoom range from 10% to 400%
            .on("zoom", (event) => {
                g.attr("transform", event.transform);
            });

        // Apply zoom behavior to svg
        svg.call(zoom);

        // Initial transform to center the graph
        const initialTransform = d3.zoomIdentity.translate(width / 2, height / 2);
        svg.call(zoom.transform, initialTransform);

        // --- Setup Simulation ---
        const simulation = d3.forceSimulation(processedData.nodes)
            .force("link", d3.forceLink(processedData.links).id(d => d.id).distance(100))
            .force("charge", d3.forceManyBody().strength(-300))
            .force("center", d3.forceCenter(0, 0)) // Center in the coordinate system
            .force("collision", d3.forceCollide().radius(30));

        // --- Draw Links ---
        const link = g.append("g")
            .attr("stroke", "#999")
            .attr("stroke-opacity", 0.6)
            .selectAll("line")
            .data(processedData.links)
            .join("line")
            .attr("stroke-width", d => Math.sqrt(d.value || 1));

        // --- Draw Nodes ---
        const node = g.append("g")
            .attr("stroke", "#fff")
            .attr("stroke-width", 1.5)
            .selectAll("circle")
            .data(processedData.nodes)
            .join("circle")
            .attr("r", 10)
            .attr("fill", d => {
                switch (d.type) {
                    case 'Goal': return '#1f77b4'; // Blue
                    case 'Initiative': return '#ff7f0e'; // Orange
                    case 'Issue': return '#d62728'; // Red
                    case 'ActionItem': return '#2ca02c'; // Green
                    default: return '#7f7f7f'; // Gray
                }
            })
            .style("cursor", "pointer")
            .call(drag(simulation));

        // --- Add Labels ---
        const labels = g.append("g")
            .attr("class", "labels")
            .selectAll("text")
            .data(processedData.nodes)
            .join("text")
            .attr("text-anchor", "middle")
            .attr("dy", "-0.5em")
            .attr("font-size", "10px")
            .attr("fill", "#333")
            .style("pointer-events", "none") // Prevent labels from interfering with drag
            .text(d => d.label || d.name || d.id);

        // --- Add Tooltips ---
        node.append("title")
            .text(d => `${d.type}: ${d.label || d.name || d.id}\n${d.description || ''}`);

        // --- Simulation Ticker ---
        simulation.on("tick", () => {
            link
                .attr("x1", d => d.source.x)
                .attr("y1", d => d.source.y)
                .attr("x2", d => d.target.x)
                .attr("y2", d => d.target.y);

            node
                .attr("cx", d => d.x)
                .attr("cy", d => d.y);

            labels
                .attr("x", d => d.x)
                .attr("y", d => d.y);
        });

        // --- Drag Functionality ---
        function drag(simulation) {
            function dragstarted(event, d) {
                if (!event.active) simulation.alphaTarget(0.3).restart();
                d.fx = d.x;
                d.fy = d.y;
            }

            function dragged(event, d) {
                d.fx = event.x;
                d.fy = event.y;
            }

            function dragended(event, d) {
                if (!event.active) simulation.alphaTarget(0);
                // Uncomment the next two lines if you want nodes to move freely after dragging
                // d.fx = null;
                // d.fy = null;
            }

            return d3.drag()
                .on("start", dragstarted)
                .on("drag", dragged)
                .on("end", dragended);
        }

        // --- Prevent zoom on double-click (optional) ---
        svg.on("dblclick.zoom", null);

        // --- Add zoom controls (optional) ---
        const zoomIn = () => {
            svg.transition().call(zoom.scaleBy, 1.5);
        };

        const zoomOut = () => {
            svg.transition().call(zoom.scaleBy, 1 / 1.5);
        };

        const resetZoom = () => {
            svg.transition().call(zoom.transform, initialTransform);
        };

        // Store zoom functions in ref for button access
        zoomFunctionsRef.current = {
            zoomIn,
            zoomOut,
            resetZoom
        };

        // --- Cleanup simulation on component unmount ---
        return () => {
            simulation.stop();
        };

    }, [processedData]);

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
            <svg ref={svgRef} style={{ cursor: 'grab' }}></svg>
            {/* Optional: Add zoom control buttons */}
            <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '5px'
            }}>
                <button
                    onClick={() => zoomFunctionsRef.current.zoomIn?.()}
                    style={{
                        padding: '5px 10px',
                        backgroundColor: '#f0f0f0',
                        border: '1px solid #ccc',
                        borderRadius: '3px',
                        cursor: 'pointer'
                    }}
                >
                    +
                </button>
                <button
                    onClick={() => zoomFunctionsRef.current.zoomOut?.()}
                    style={{
                        padding: '5px 10px',
                        backgroundColor: '#f0f0f0',
                        border: '1px solid #ccc',
                        borderRadius: '3px',
                        cursor: 'pointer'
                    }}
                >
                    −
                </button>
                <button
                    onClick={() => zoomFunctionsRef.current.resetZoom?.()}
                    style={{
                        padding: '5px 10px',
                        backgroundColor: '#f0f0f0',
                        border: '1px solid #ccc',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '10px'
                    }}
                >
                    Reset
                </button>
            </div>
        </div>
    );
};

export default KnowledgeGraphVisualization;